<script setup>
import { computed, ref } from 'vue'
import { useDialogPluginComponent } from 'quasar'
import { getI18n } from '@/plugins/i18n'

import { exchangeRateSourceEnum } from '@/stores/portal.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import { useCurrency } from '@/composables/currency'
import { useNotify } from '@/composables/notify'
import {
  convertWithRates,
  currencyCode,
  exchangeRateForCurrency,
  isReferenceCurrency,
  isSameExchangeRate,
  manualTableExchangeRate,
} from '@/composables/exchange-rate'

import ExchangeRatePanel from '@/components/shared/ExchangeRatePanel.vue'
import InfoBox from '@/components/shared/InfoBox.vue'

const AMOUNT_DECIMALS = 6

const props = defineProps({
  fromRef: {
    type: Number,
    required: true,
  },
  toRef: {
    type: Number,
    required: true,
  },
  amounts: {
    type: Object,
    required: true,
  },
})

defineEmits([...useDialogPluginComponent.emits])

const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent()

const i18n = getI18n()
const webSiteStore = useWebSiteStore()
const { getCurrencyString } = useCurrency()
const { notifySuccess } = useNotify()

const foreignRefs = [...new Set([props.fromRef, props.toRef])].filter(ref => !isReferenceCurrency(ref))
const rates = ref(Object.fromEntries(foreignRefs.map(ref => [ref, exchangeRateForCurrency(ref)])))

const rateOf = (currencyRef) => isReferenceCurrency(currencyRef) ? 1 : Number(rates.value[currencyRef].exchange_rate) || null

const converted = computed(() => {
  const fromRate = rateOf(props.fromRef)
  const toRate = rateOf(props.toRef)
  return Object.fromEntries(Object.entries(props.amounts).map(([field, amount]) => {
    if (amount == null || amount === '') return [field, amount]
    const value = convertWithRates(amount, fromRate, toRate)
    return [field, value == null ? null : Number(value.toFixed(AMOUNT_DECIMALS))]
  }))
})
const canConvert = computed(() => Object.entries(converted.value).every(([field, value]) => value != null || props.amounts[field] == null || props.amounts[field] === ''))

const shouldConvert = ref(canConvert.value)

const formatAmounts = (amounts) => Object.values(amounts)
  .filter(amount => amount != null && amount !== '')
  .map(amount => getCurrencyString(amount, currencyCode(props.toRef)))
  .join(' / ')

const isNewManualDefault = (currencyRef) => {
  const rate = rates.value[currencyRef]
  return rate.exchange_rate_source === exchangeRateSourceEnum.MANUAL &&
    rate.exchange_rate != null &&
    !isSameExchangeRate(rate, manualTableExchangeRate(currencyRef))
}

const saveManualRatesAsDefaultAsync = async() => {
  if (!webSiteStore.can(webSiteStore.permissionFlagEnum.COMPANY_SETTINGS)) return
  const currencyRefs = foreignRefs.filter(isNewManualDefault)
  if (!currencyRefs.length) return
  await Promise.all(currencyRefs.map(currencyRef => webSiteStore.updateCurrencyAsync(currencyRef, {
    exchange_rate: Number(rates.value[currencyRef].exchange_rate),
    exchange_rate_date: rates.value[currencyRef].exchange_rate_date,
  })))
  await webSiteStore.fetchCurrenciesAsync({ all: true }, true)
  notifySuccess(i18n.t('company.exchangeRates.manualRateSavedAsDefault', { currencies: currencyRefs.map(currencyCode).join(', ') }))
}

const onConfirm = async() => {
  await saveManualRatesAsDefaultAsync()
  onDialogOK(shouldConvert.value && canConvert.value ? converted.value : props.amounts)
}
</script>
<template>
  <q-dialog
    ref="dialogRef"
    persistent
    class="dialog-xs"
    @hide="onDialogHide"
  >
    <q-card>
      <q-card-section class="row items-center no-wrap">
        <q-avatar icon="mdi-swap-horizontal" color="primary" text-color="white" />
        <div class="text-h6 q-ml-sm">{{ i18n.t('company.exchangeRates.currencyChangeTitle', { currency: currencyCode(props.toRef) }) }}</div>
      </q-card-section>
      <q-card-section class="q-pt-none">
        {{ i18n.t(webSiteStore.can(webSiteStore.permissionFlagEnum.COMPANY_SETTINGS) ? 'company.exchangeRates.currencyChangeMessageSavesManual' : 'company.exchangeRates.currencyChangeMessage') }}
      </q-card-section>
      <q-card-section class="q-pt-none">
        <div v-for="foreignRef in foreignRefs" :key="foreignRef" class="q-mb-sm">
          <div class="text-caption text-grey-7">{{ currencyCode(foreignRef) }}</div>
          <exchange-rate-panel
            :model-value="rates[foreignRef]"
            :currency-ref="foreignRef"
            :no-rate-hint="i18n.t('company.exchangeRates.enterRate')"
            @update:model-value="rates[foreignRef] = $event"
          />
        </div>
        <q-toggle
          v-model="shouldConvert"
          :label="i18n.t('company.exchangeRates.convertAmounts')"
          :disable="!canConvert"
          color="primary"
        />
        <info-box :icon="shouldConvert && canConvert ? 'mdi-swap-horizontal' : 'mdi-equal'">
          <i18n-t
            :keypath="shouldConvert && canConvert ? 'company.exchangeRates.convertAmountsOn' : 'company.exchangeRates.convertAmountsOff'"
            tag="div"
            scope="global"
          >
            <template #currency><b>{{ currencyCode(props.toRef) }}</b></template>
            <template #amounts><b>{{ formatAmounts(shouldConvert && canConvert ? converted : props.amounts) }}</b></template>
          </i18n-t>
        </info-box>
      </q-card-section>
      <q-card-actions align="right">
        <q-btn
          flat
          :label="i18n.t('common.cancel')"
          @click="onDialogCancel"
        />
        <q-btn
          flat
          color="primary"
          :label="i18n.t('common.confirm')"
          @click="onConfirm"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
